import type { VistaCard } from "../contratos";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { ComoLer } from "./ComoLer";
import { Dados } from "./Dados";
import { DeOndeVeio } from "./DeOndeVeio";
import { Leituras } from "./Leituras";
import { OutroLado } from "./OutroLado";
import { Pergunta } from "./Pergunta";
import { Rodape } from "./Rodape";
import estilos from "./card.module.css";
import { caminho } from "./util";

export type NomeBloco =
  "Origem" | "Dados" | "Leituras" | "Pesquisa" | "Como ler" | "Pergunta" | "Sobre";

/**
 * O card, na ordem fixa de blocos da vista (a mesma do `card.txt` do servidor).
 *
 * Estes componentes são o template fixo da web (ADR 0010): todo texto vem da vista montada no
 * servidor e entra como texto. O modelo nunca escreve HTML (invariante 5). Na web o card é
 * montado pela mesa (`Avisos` e `Blocos`); este conjunto completo serve aos testes do card.
 * @internal
 */
export function Card(props: { vista: VistaCard; textoUrl: string; recarregar: () => void }) {
  const { vista } = props;
  return (
    <article aria-labelledby="alegacao">
      <p className={estilos.rotulo}>Afirmação analisada (em forma neutra)</p>
      <h1 id="alegacao" tabIndex={-1}>
        {vista.alegacao}
      </h1>
      <Avisos vista={vista} />
      <Blocos {...props} />
    </article>
  );
}

/** Versões e divergências entre composições, logo abaixo da alegação. */
export function Avisos({ vista }: { vista: VistaCard }) {
  return (
    <>
      {!vista.versao_mais_recente ? (
        <p className={estilos.destaque} role="note">
          Esta é a versão {vista.versao} da análise. Existe uma versão mais recente.
        </p>
      ) : (
        vista.outras_versoes.length > 0 && (
          <p className={estilos.destaque} role="note">
            Esta análise mudou depois de uma contestação; esta é a versão {vista.versao}.
            Anteriores:{" "}
            {vista.outras_versoes.map((v, i) => (
              <span key={v.versao}>
                {v.url ? <Link to={caminho(v.url)}>versão {v.versao}</Link> : `versão ${v.versao}`}
                {i < vista.outras_versoes.length - 1 ? ", " : ""}
              </span>
            ))}
            .
          </p>
        )
      )}
      {(vista.divergencias ?? []).map((d) => (
        <p key={d.url} className={estilos.destaque} role="note">
          Outro perfil de modelos (<strong>{d.perfil}</strong>) chegou a outro resultado para esta
          mesma afirmação em {d.data}: {d.resultado}.{" "}
          <Link to={caminho(d.url)}>Ver a análise desse perfil</Link>.
        </p>
      ))}
    </>
  );
}

/**
 * Os blocos do card, na ordem fixa. `envolver` deixa a mesa pôr o botão de evidência ao lado de
 * cada bloco sem mudar o bloco.
 */
export function Blocos(props: {
  vista: VistaCard;
  textoUrl: string;
  recarregar: () => void;
  envolver?: (nome: NomeBloco, bloco: ReactNode) => ReactNode;
}) {
  const { vista } = props;
  const envolver = props.envolver ?? ((_: NomeBloco, bloco: ReactNode) => bloco);
  // Blocos 3 e 4 só existem quando há leituras e pesquisa; os seguintes se renumeram.
  const comLeituras = (vista.leituras ?? []).length > 0;
  const comOutroLado = (vista.outro_lado ?? []).length > 0;
  const comoLer = 3 + Number(comLeituras) + Number(comOutroLado);
  return (
    <>
      {envolver("Origem", <DeOndeVeio vista={vista} />)}
      {envolver("Dados", <Dados vista={vista} />)}
      {comLeituras && envolver("Leituras", <Leituras vista={vista} numero={3} />)}
      {comOutroLado &&
        envolver("Pesquisa", <OutroLado vista={vista} numero={3 + Number(comLeituras)} />)}
      {envolver("Como ler", <ComoLer vista={vista} numero={comoLer} />)}
      {envolver("Pergunta", <Pergunta vista={vista} numero={comoLer + 1} />)}
      {envolver(
        "Sobre",
        <Rodape
          vista={vista}
          numero={comoLer + 2}
          textoUrl={props.textoUrl}
          recarregar={props.recarregar}
        />,
      )}
    </>
  );
}
